import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  approvedVendor,
  blockedVendor,
  getVendors,
  unblockedVendor,
  createVendor,
  deleteVendor,
  updateVendor,
  pendingVendor
} from "../../State/Vendor/Action";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Delete, Edit, Add as AddIcon, Print, PictureAsPdf, FileDownload } from "@mui/icons-material"; // import AddIcon
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import EditIcon from "@mui/icons-material/Edit"
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";

const Vendors = () => {
  const dispatch = useDispatch();
  const { adminVendor } = useSelector((store) => store);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [openAddVendorModal, setOpenAddVendorModal] = useState(false);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");
  // Add this state for selection
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    fullAddress: "",
    gstNo: "",
  });

  useEffect(() => {
    dispatch(getVendors());
    setShowPassword(false)
  }, [dispatch, openAddVendorModal]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    // Allow only digits in phone
    if (name === "phone" && !/^\d*$/.test(value)) return;


    // Validate email
    if (name === "email" && value && !/^\S+@\S+\.\S+$/.test(value)) {
      setError("Invalid email");
    } else {
      setError("");
    }

    setFormData({ ...formData, [name]: value });
  };
  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      formData.name?.trim() === "" ||
      formData.email?.trim() === "" ||
      formData.phone?.trim() === "" ||
      formData.fullAddress?.trim() === ""
    ) {
      toast.error("Please fill all the fields");
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error("Enter valid 10-digit phone number");
      return;
    }
    if (formData.name?.length < 3 || formData.name?.length > 50) {
      toast.error("Name should be between 3 to 50 characters");
      return;
    }

    if (formData.fullAddress.length < 10 || formData.fullAddress.length > 100) {
      toast.error("Address should be between 10 to 100 characters");
      return;
    }

    if (/^\S+@\S+\.\S+$/.test(formData.email.length)) {
      setError("Invalid email");
      return
    }

    if (error !== "") {
      return null;
    }

    if (editingId) {
      if (formData.password.trim()) {
        if (formData.password.length < 6 || formData.password.length > 15) {
          toast.error("Password should be between 6 to 15 characters");
          return;
        }
      }
      dispatch(updateVendor(editingId, formData, handleCancle))
    } else {
      if (formData.password?.trim() === "") {
        toast.error("Password is required !!");
        return;
      }
      if (formData.password?.length < 6 || formData.password?.length > 15) {
        toast.error("Password should be between 6 to 15 characters");
        return;
      }
      dispatch(createVendor(formData, handleCancle))
    }
  };


  const confirmDelete = () => {
    dispatch(deleteVendor(vendorToDelete, setOpenDialog));
    setVendorToDelete(null);
  };

  const handleDeleteClick = (id) => {
    setVendorToDelete(id);
    setOpenDialog(true);
  };

  const handleUpdateClick = (item) => {
    setFormData({
      name: item.name,
      email: item.email,
      phone: item.phone,
      password: "",
      fullAddress: item?.fullAddress,
      gstNo: item?.gstNo
    });
    setEditingId(item.id);
    setOpenAddVendorModal(true);
  };

  const handleApprove = () => {
    if (selectedVendor) {
      if (selectedVendor.approved) {
        dispatch(pendingVendor(selectedVendor.id, setApproveDialogOpen));
      } else {
        dispatch(approvedVendor(selectedVendor.id, setApproveDialogOpen));
      }
      setSelectedVendor(null);
    }
  };

  const handleStatusChange = () => {
    if (selectedVendor) {
      if (selectedVendor.isBlocked) {
        dispatch(unblockedVendor(selectedVendor.id, setConfirmDialogOpen));
      } else {
        dispatch(blockedVendor(selectedVendor.id, setConfirmDialogOpen));
      }
      setSelectedVendor(null);
    }
  };

  const handleOpenDialog = (vendor, type) => {
    setSelectedVendor(vendor);
    if (type === "approve") {
      setApproveDialogOpen(true);
    } else if (type === "block") {
      setConfirmDialogOpen(true);
    }
  };

  const handleCancle = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      password: "",
      fullAddress: "",
      gstNo: ""
    });
    setEditingId(null);
    setOpenAddVendorModal(false);
  }

  const writeAction = PermissionService.has({ category: "Admin", page: "Vendors", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 70,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    {
      field: "createdAt",
      headerName: "Create Date",
      flex: 1,
      renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----",
    },
    { field: "name", headerName: "Name", flex: 1, },
    { field: "email", headerName: "Email", flex: 1.5, },
    { field: "phone", headerName: "Phone", flex: 1, },
    { field: "gstNo", headerName: "Gst No.", flex: 1, },
    { field: "fullAddress", headerName: "Address", flex: 1.5, },
    {
      field: "approved",
      headerName: "Approval Status",
      flex: 1.5,
      renderCell: (params) => (
        // <>
        //   <span
        //     className="text-white rounded-full"
        //     style={{
        //       fontSize: "0.75rem",
        //       padding: "2px 8px",
        //       minWidth: 60,
        //       display: "inline-block",
        //       textAlign: "center",
        //       background:
        //         params.value
        //           ? "green"
        //           : "gray",
        //       color: "#fff",
        //       fontWeight: 500,
        //       borderRadius: "12px",
        //     }}
        //   >
        //     {params.value ? "APPROVED" : "PENDING"}
        //   </span>
        //   {!params.row.approved && writeAction && (
        //     <IconButton
        //       size="small"
        //       onClick={() => handleOpenDialog(params.row, "approve")}
        //     >
        //       <EditIcon fontSize="small" />
        //     </IconButton>
        //   )}
        // </>
        <div
          disabled={params.row.approved}
          disableSelectionOnClick={params.row.approved}
          onClick={() => handleOpenDialog(params.row, "approve")}
          style={{
            width: 100,
            height: 18,
            backgroundColor: params.value ? "#22c55e" : "#ef4444",
            display: "flex",
            alignItems: "center",
            justifyContent: params.value ? "flex-end" : "flex-start",
            // padding: "8px",
            cursor: "pointer",
            transition: "0.3s",
            borderRadius: 50,
            marginTop: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexDirection: params.value ? "row-reverse" : "row",
              transition: "0.3s"
            }}
          >
            {/* Square Toggle Block */}
            <div
              style={{
                width: 18,
                height: 18,
                backgroundColor: "#fff",
                borderRadius: 50,
                transition: "0.3s"
              }}
            />

            {/* Name */}
            <span
              style={{
                color: "#fff",
                fontWeight: 600,
                fontSize: 13,
                minWidth: 60,
                textAlign: "center",
                // paddingLeft: 5,
                // paddingRight: 5,
              }}
            >
              {params.value ? "APPROVED" : "PENDING"}
            </span>
          </div>
        </div>

      )
    },
    {
      field: "isBlocked",
      headerName: "Vendor Status",
      flex: 1.5,
      renderCell: (params) => (
        // <>
        //   <span
        //     className="text-white rounded-full"
        //     style={{
        //       fontSize: "0.75rem",
        //       padding: "2px 8px",
        //       minWidth: 60,
        //       display: "inline-block",
        //       textAlign: "center",
        //       background:
        //         !params.value
        //           ? "green"
        //           : "red",
        //       color: "#fff",
        //       fontWeight: 500,
        //       borderRadius: "12px",
        //     }}
        //   >
        //     {params.value ? "BLOCKED" : "UNBLOCKED"}
        //   </span>
        //   {writeAction && (
        //     <IconButton
        //       size="small"
        //       onClick={() => handleOpenDialog(params.row, "block")}
        //     >
        //       <EditIcon fontSize="small" />
        //     </IconButton>
        //   )}
        // </>
        <div
          onClick={() => handleOpenDialog(params.row, "block")}
          style={{
            width: 110,
            height: 18,
            backgroundColor: !params.value ? "#22c55e" : "#ef4444",
            display: "flex",
            alignItems: "center",
            justifyContent: !params.value ? "flex-end" : "flex-start",
            // padding: "8px",
            cursor: "pointer",
            transition: "0.3s",
            borderRadius: 50,
            marginTop: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexDirection: !params.value ? "row-reverse" : "row",
              transition: "0.3s"
            }}
          >
            {/* Square Toggle Block */}
            <div
              style={{
                width: 18,
                height: 18,
                backgroundColor: "#fff",
                borderRadius: 50,
                transition: "0.3s"
              }}
            />

            {/* Name */}
            <span
              style={{
                color: "#fff",
                fontWeight: 600,
                fontSize: 13,
                minWidth: 60,
                textAlign: "center",
                // paddingLeft: 5,
                // paddingRight: 5,
              }}
            >
              {params.value ? "BLOCKED" : "UNBLOCKED"}
            </span>
          </div>
        </div>
      ),
    },
    ...(writeAction ? [
      {
        field: "actions",
        headerName: "Action",
        flex: 1,
        renderCell: (params) => (
          <Box display="flex" gap={0} sx={{ p: 0, m: 0, minHeight: 0 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleUpdateClick(params.row)}
              >
                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleDeleteClick(params.row.id)}
              >
                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )
      }
    ] : []),

  ];


  const filteredRows = adminVendor?.vendors?.response?.filter(row => {
    const query = searchText.toLowerCase();
    return (
      row.name?.toLowerCase().includes(query) ||
      row.email?.toLowerCase().includes(query) ||
      row.phone?.toLowerCase().includes(query) ||
      row.fullAddress?.toLowerCase().includes(query)
    );
  });
  const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => [index + 1,
    row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name, row.email, row.phone, row.gstNo, row.fullAddress, row.approved, row.isBlocked
    ].join(',')).join('\n');
    const headers = ['S.No.', 'Created Date', 'Name', 'Email', 'Phone', "Gst No.", 'Address', 'Approved', 'Blocked'].join(',');
    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'vendors.csv';
    link.click();
  };
  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Employee List", 14, 15);
    const tableColumn = ['S.No.', 'Created Date', 'Name', 'Email', 'Phone', 'Gst No.', 'Address', 'Approved', 'Blocked'];
    const tableRows = exportRows.map((row, index) => [index + 1, row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name, row.email, row.phone, row.gstNo, row.fullAddress, row.approved, row.isBlocked]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
    });
    doc.save('vendors.pdf');
  };
  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = ['S.No.', 'Created Date', 'Name', 'Email', 'Phone', 'Gst No.', 'Address', 'Approved', 'Blocked'];
    const tableContent = exportRows.map((row, index) => [index + 1, row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name, row.email, row.phone, row.gstNo, row.fullAddress, row.approved, row.isBlocked]);
    const html = `
      <html>
        <head>
          <title>Vendor List</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
            tr:nth-child(even) { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          <h2>Vendor List</h2>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableContent.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };


  return (
    <div>
      <Card className="p-4" sx={{ borderRadius: 2, boxShadow: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <CardHeader
            title={<Typography variant="h6" className="text-gray-700">VENDORS</Typography>}
            sx={{ p: 0 }}
          />
          {writeAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                sx={{
                  background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                    transform: "scale(1.02)",
                  },
                  cursor: "pointer",
                  p: 1,
                  borderRadius: '50%',
                }}
                onClick={() => {
                  setOpenAddVendorModal(true);
                  setEditingId(null);
                }}
              >
                <AddIcon />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
              >
                Add Vendor
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Print
            </Button>
          </Box>
          <Box>
            <TextField
              label="Search Vendors"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              sx={{ width: { xs: '100%', sm: '300px' } }}
            />
          </Box>
        </Box>
        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 75, 100]}
            checkboxSelection
            onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
            rowSelectionModel={selectedRowIds}
            disableSelectionOnClick
            autoHeight={true}
            density="compact"
            experimentalFeatures={{ virtualization: true }}
            loading={adminVendor.loading}
            sx={{
              borderRadius: 2,
              fontSize: "0.8rem",
              "& .MuiDataGrid-cell": {
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
                paddingTop: "4px",
                paddingBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#449AEF",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "0.8rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "36px !important",
                minHeight: "36px !important",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
            getRowId={(row) => row.id}
          />
        </Box>
      </Card>

      {/* Confirm Block/Unblock Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {selectedVendor?.isBlocked ? "unblock" : "block"} this vendor?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleStatusChange} color="error" autoFocus>
            {adminVendor.loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (selectedVendor?.isBlocked ? "Unblock" : "Block")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
      >
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {!selectedVendor?.approved ? "approved" : "pending"} this vendor?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleApprove} color="success" autoFocus>
            {adminVendor.loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (!selectedVendor?.approved ? "Approved" : "Pending")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace the form rendering with a Dialog: */}
      <Dialog
        open={openAddVendorModal}
        onClose={handleCancle}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            mt: 1,
            mb: 0.5,
            pb: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {editingId ? "UPDATE VENDOR" : "ADD NEW VENDOR"}
          <IconButton onClick={handleCancle} size="small" sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1 }}>
          <Card className="mb-4 mt-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      id="name"
                      name="name"
                      label="Full Name"
                      fullWidth
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="given-name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      id="email"
                      name="email"
                      label="Email"
                      type="email"
                      fullWidth
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      error={!!error}
                      helperText={error}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      id="phone"
                      name="phone"
                      label="Phone Number"
                      fullWidth
                      value={formData.phone}
                      onChange={handleChange}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={editingId ? "New Password" : "Password"}
                      name="password"
                      placeholder={editingId ? "Enter New Password" : "Enter Password"}
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      fullWidth
                      required={!editingId}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <EyeSlashIcon className="h-5 w-5" />
                              ) : (
                                <EyeIcon className="h-5 w-5" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      required
                      id="fullAddress"
                      name="fullAddress"
                      label="Full Address"
                      fullWidth
                      multiline
                      rows={2}
                      value={formData.fullAddress}
                      onChange={handleChange}
                      autoComplete="given-fullAddress"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={editingId}
                      required
                      id="gstNo"
                      name="gstNo"
                      label="GST No."
                      fullWidth
                      value={formData.gstNo}
                      onChange={handleChange}
                      inputProps={{ maxLength: 15 }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2, }}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={adminVendor.loading}
                      sx={{
                        background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                          boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                          transform: "scale(1.02)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      {adminVendor.loading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Processing...
                        </>
                      ) : (editingId ? "Update Vendor" : "Add Vendor")}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancle}
                      sx={{
                        color: "#449AEF",
                        borderColor: "#449AEF",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#449AEF",
                          color: "#fff",
                          borderColor: "#449AEF",
                          boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>

                </Grid>
              </form>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this Vendor?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {adminVendor.loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : ("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>


    </div>
  );
};

export default Vendors;
